
import React, { useState, useEffect } from 'react';
import { Search, Filter, Plus, Tag, ExternalLink, Globe, Info, AlertTriangle, TrendingUp, BarChart3, X, Loader2 } from 'lucide-react';
import { toast } from '../components/Toast';
import { GoalMode, Environment, ConnectionConfig } from '../types';
import EmptyState from '../components/EmptyState';

interface InventoryProps {
  goalMode: GoalMode;
  activeEnv: Environment | null;
}

interface ContentItem {
  id: string;
  title: string;
  url: string;
  type: string;
  publish_date?: string;
  topic_tags: string[];
  audience_tags: string[];
  funnel_stage: string;
  owner?: string;
}

// Generate SEO metrics for content items
const generateSEOMetrics = (contentId: string) => {
  // In production, this would fetch real ranking data from Search Console
  const hash = contentId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return {
    rankingKeywords: Math.floor((hash % 15) + 3), // 3-18 keywords
    estimatedTraffic: Math.floor((hash % 500) + 50), // 50-550 visits/mo
    seoScore: Math.floor((hash % 40) + 60), // 60-100 score
  };
};

const Inventory: React.FC<InventoryProps> = ({ goalMode, activeEnv }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [seoHealth, setSeoHealth] = useState<any>(null);
  const [contentItems, setContentItems] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newContent, setNewContent] = useState({
    title: '',
    url: '',
    type: 'page',
    funnel_stage: 'awareness',
    topic_tags: [] as string[],
    audience_tags: [] as string[],
    owner: ''
  });

  // Fix: Cast Object.values to ConnectionConfig[] to resolve "Property 'connected' does not exist on type 'unknown'"
  const hasAnyData = activeEnv ? (Object.values(activeEnv.connections) as ConnectionConfig[]).some(v => v.connected) : false;

  // Fetch content items from database
  useEffect(() => {
    if (!activeEnv || activeEnv.isSample) return;

    const fetchContent = async () => {
      setLoading(true);
      try {
        const response = await fetch(`/api/content?environmentId=${activeEnv.id}`, {
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          setContentItems(data.content || []);
        }
      } catch (error) {
        console.error('Failed to fetch content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [activeEnv?.id, activeEnv?.isSample]);

  // Fetch overall SEO health
  useEffect(() => {
    if (!activeEnv || activeEnv.isSample) return;

    const fetchSEOHealth = async () => {
      try {
        const response = await fetch('/api/seo/research', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'audit',
            domain: 'example.com'
          })
        });

        if (response.ok) {
          const data = await response.json();
          setSeoHealth(data.data);
        }
      } catch (error) {
        console.error('Failed to fetch SEO health:', error);
      }
    };

    fetchSEOHealth();
  }, [activeEnv?.id, activeEnv?.isSample]);

  const handleAddContent = async () => {
    if (!activeEnv || !newContent.title || !newContent.url) {
      toast.warning('Please fill in title and URL');
      return;
    }

    try {
      const response = await fetch('/api/content', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          environmentId: activeEnv.id,
          ...newContent
        })
      });

      if (response.ok) {
        const data = await response.json();
        setContentItems([...contentItems, data]);
        setShowAddModal(false);
        setNewContent({
          title: '',
          url: '',
          type: 'page',
          funnel_stage: 'awareness',
          topic_tags: [],
          audience_tags: [],
          owner: ''
        });
      }
    } catch (error) {
      console.error('Failed to add content:', error);
      toast.error('Failed to add content');
    }
  };

  const filteredContent = contentItems.filter(item =>
    item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.url.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!activeEnv) return <EmptyState />;

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 pb-4">
        <div>
          <h2 className="text-3xl font-bold text-slate-900">Content Inventory</h2>
          <p className="text-slate-500 mt-1 flex items-center gap-2 text-sm italic">
            Centralized hub for all digital assets.
          </p>
        </div>
        <div className="flex gap-2">
          <button className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-semibold shadow-sm">
            <Globe size={16} /> Sitemap Crawl
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#003B71] text-white rounded-lg text-sm font-semibold shadow-sm hover:bg-[#002952] transition-colors"
          >
            <Plus size={16} /> Add Content
          </button>
        </div>
      </div>

      {!hasAnyData && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 flex items-center gap-4 text-amber-800">
          <AlertTriangle className="flex-shrink-0" />
          <div className="text-sm">
            <p className="font-bold">No active data connections.</p>
            <p className="opacity-80">Requires a data source for {activeEnv.name}.</p>
          </div>
        </div>
      )}

      {/* SEO Health Summary */}
      {seoHealth && hasAnyData && (
        <div className="bg-gradient-to-r from-[#003B71] to-[#0B2341] rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-black text-lg flex items-center gap-2">
              <BarChart3 size={20} className="text-[#FFCD00]" />
              Content SEO Health
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-3xl font-black">{seoHealth.score}</span>
              <span className="text-sm text-white/60">/100</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {seoHealth.issues.slice(0, 3).map((issue: any, idx: number) => (
              <div key={idx} className="bg-white/10 backdrop-blur rounded-xl p-3 border border-white/20">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-[9px] font-bold px-2 py-0.5 rounded uppercase ${
                    issue.severity === 'high' ? 'bg-red-500/20 text-red-200' :
                    issue.severity === 'medium' ? 'bg-yellow-500/20 text-yellow-200' :
                    'bg-blue-500/20 text-blue-200'
                  }`}>
                    {issue.severity}
                  </span>
                  <span className="text-[10px] text-white/60">{issue.category}</span>
                </div>
                <p className="text-xs font-bold leading-tight">{issue.issue}</p>
                <p className="text-[10px] text-white/80 mt-2">{issue.affectedPages} pages affected</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={`bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden ${!hasAnyData ? 'opacity-30 grayscale pointer-events-none' : ''}`}>
        <div className="p-4 border-b border-slate-100 flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input 
              type="text" 
              placeholder="Search..."
              className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm outline-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <button className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 text-slate-600 rounded-lg text-sm font-medium">
              <Filter size={16} /> Filters
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/50 text-slate-500 text-[10px] uppercase font-bold tracking-widest border-b border-slate-100">
                <th className="px-6 py-4">Title / URL</th>
                <th className="px-6 py-4">Type</th>
                <th className="px-6 py-4">Stage</th>
                <th className="px-6 py-4">Topics</th>
                <th className="px-6 py-4">SEO Score</th>
                <th className="px-6 py-4">Traffic Est.</th>
                <th className="px-6 py-4">Last Updated</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <Loader2 className="animate-spin mx-auto text-slate-400" size={32} />
                    <p className="text-slate-500 mt-3 text-sm">Loading content...</p>
                  </td>
                </tr>
              ) : filteredContent.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <p className="text-slate-500 text-sm">
                      {searchTerm ? 'No content matches your search' : 'No content added yet. Click "Add Content" to get started.'}
                    </p>
                  </td>
                </tr>
              ) : filteredContent.map((item) => {
                const seoMetrics = generateSEOMetrics(item.id);
                return (
                  <tr key={item.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="px-6 py-5">
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-slate-900 mb-0.5">{item.title}</span>
                        <span className="text-xs text-slate-400 flex items-center gap-1">
                          {item.url} <ExternalLink size={10} />
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-md ${
                        item.type === 'video' ? 'bg-red-50 text-red-700' :
                        item.type === 'news' ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {item.type}
                      </span>
                    </td>
                    <td className="px-6 py-5 capitalize text-xs">{item.funnel_stage}</td>
                    <td className="px-6 py-5">
                      <div className="flex flex-wrap gap-1 max-w-[200px]">
                        {item.topic_tags.map(tag => (
                          <span key={tag} className="text-[10px] bg-[#FFCD00]/10 text-[#003B71] px-1.5 py-0.5 rounded border border-[#FFCD00]/20 font-medium">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-2">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center font-black text-sm ${
                          seoMetrics.seoScore >= 80 ? 'bg-green-100 text-green-700' :
                          seoMetrics.seoScore >= 60 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {seoMetrics.seoScore}
                        </div>
                        <div className="text-[10px]">
                          <p className="text-slate-400">Ranking for</p>
                          <p className="font-bold text-slate-700">{seoMetrics.rankingKeywords} keywords</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-1 text-xs font-bold text-slate-700">
                        <TrendingUp size={12} className="text-green-600" />
                        {seoMetrics.estimatedTraffic}/mo
                      </div>
                      <p className="text-[9px] text-slate-400 mt-0.5">from organic</p>
                    </td>
                    <td className="px-6 py-5 text-sm text-slate-500">{item.publish_date}</td>
                    <td className="px-6 py-5 text-right">
                      <button className="text-xs font-bold text-[#003B71] hover:underline">Edit</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Inventory;
